import { Component, ElementRef, Input, inject, ChangeDetectorRef, ViewChild } from '@angular/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from '@services/toast/toast.service';
import { ApiClientService } from '@services/api-client';
import { MeasurementData, ParameterRecord } from '../../models/measurement-view.types';
import { ControlSettings } from '../control-panel/control-panel.component';
import { ExportType } from 'app/enums/export-type.enum';
import { HttpResponse } from '@angular/common/http';
import { Permission } from 'app/enums/permission.enum';

@Component({
    selector: 'app-report-actions',
    templateUrl: './report-actions.component.html',
    styleUrl: './report-actions.component.scss',
    standalone: false
})
export class ReportActionsComponent {
    private readonly translateService = inject(TranslateService);
    private readonly toastService = inject(ToastService);
    private readonly apiClientService = inject(ApiClientService);
    private readonly changeDetectorRef = inject(ChangeDetectorRef);

    @ViewChild('reportContent') private reportContent?: ElementRef<HTMLDivElement>;

    @Input() public controlSettings!: ControlSettings;
    @Input() public measurementData: MeasurementData | null = null;
    @Input() public selectedMeasurementId: string | null = null;

    public permissions = Permission;
    public downloading = {
        pdf: false,
        excel: false,
        csv: false,
        raw: false
    };

    // Helper methods to extract report data
    public getFormattedDate(): string {
        if (!this.measurementData?.meta?.date) {
            return '-';
        }
        // Convert YYMMDD to readable format
        const dateStr = this.measurementData.meta.date;
        if (dateStr.length === 6) {
            const year = '20' + dateStr.substring(0, 2);
            const month = dateStr.substring(2, 4);
            const day = dateStr.substring(4, 6);
            return `${year}-${month}-${day}`;
        }
        return dateStr;
    }

    public getFrameCount(): number {
        if (!this.measurementData?.data) {
            return 0;
        }
        return this.measurementData.data.filter((item) => item.type === 'frame').length;
    }

    public getAnnotation(): string {
        // Check if there are any marker annotations
        if (this.measurementData?.dynamicData?.marker_annotations?.length) {
            return this.measurementData.dynamicData.marker_annotations
                .map((ann) => `M${ann.marker_num}: ${ann.annotation ?? '-'}`)
                .join('; ');
        }
        return '-';
    }

    public getAutoMode(): string {
        //const firstParam = this.getFirstParameterRecord();
        //return firstParam?.mode || '-';
        return this.getYesOrNo(this.measurementData?.meta?.isAutoSetup);
    }

    public getSupramaximal(): string {
        return this.getYesOrNo(this.measurementData?.meta?.auto_setup?.supramaximal ?? false);
    }

    public getStimulationCurrent(): string | number {
        return this.measurementData?.meta?.initial_settings?.pulse_current ?? '-';
        //const firstParam = this.getFirstParameterRecord();
        //return firstParam?.stimulus_current ?? '-';
    }

    public getPulseWidth(): string | number {
        return this.measurementData?.meta?.initial_settings?.pulse_width ?? '-';
        //const firstParam = this.getFirstParameterRecord();
        //return firstParam?.stimulus_pulse_width ?? '-';
    }

    public getInterval(): string | number {
        if (!this.measurementData?.meta?.initial_settings) {
            return '-';
        }

        const { meta } = this.measurementData;
        const initialSettings = meta.initial_settings;
        const { device } = meta;

        const tofValue = this.dsToS(initialSettings.tof_interval);
        const ptcValue = this.dsToS(initialSettings.ptc_interval ?? 0);
        const stValue = this.dsToS(initialSettings.st_interval ?? 0);

        const tofUnit = this.getIntervalUnitForValue(initialSettings.tof_interval, device);
        const ptcUnit = this.getIntervalUnitForValue(initialSettings.ptc_interval ?? 0, device);
        const stUnit = this.getIntervalUnitForValue(initialSettings.st_interval ?? 0, device);

        const uniformUnits = this.areInitialUnitsUniform();

        if (uniformUnits) {
            return `TOF: ${tofValue}, PTC: ${ptcValue}, ST: ${stValue}`;
        } else {
            return `TOF: ${tofValue} ${tofUnit}, PTC: ${ptcValue} ${ptcUnit}, ST: ${stValue} ${stUnit}`;
        }
    }

    public getIntervalUnit(): string {
        if (!this.measurementData?.meta?.initial_settings) {
            return '(s)';
        }

        const uniformUnits = this.areInitialUnitsUniform();
        if (uniformUnits) {
            const { meta } = this.measurementData;
            const unit = this.getIntervalUnitForValue(meta.initial_settings.tof_interval, meta.device);
            return `(${unit})`;
        }

        return '';
    }

    public getYesOrNo(value: boolean | null | undefined): string {
        if (value === true) {
            return this.translateService.instant('BUTTONS.YES') as string;
        } else if (value === false) {
            return this.translateService.instant('BUTTONS.NO') as string;
        } else {
            return '-';
        }
    }

    private getFirstParameterRecord(): ParameterRecord | null {
        if (!this.measurementData?.data) {
            return null;
        }
        const record = this.measurementData.data.find((item) => item.type === 'parameter');
        return record?.type === 'parameter' ? record : null;
    }

    /**
     * Convert deciseconds to seconds (NextGen intervals are stored in deciseconds)
     */
    private dsToS(ds: number): number {
        return Math.round(ds / 10);
    }

    /**
     * Get the unit for a specific interval value based on device type
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    private getIntervalUnitForValue(time: number, device: 'Classic' | 'NextGen'): string {
        return 's';
    }

    /**
     * Check if all initial interval units are uniform (same unit)
     */
    private areInitialUnitsUniform(): boolean {
        if (!this.measurementData?.meta?.initial_settings) {
            return true;
        }

        const { meta } = this.measurementData;
        const initialSettings = meta.initial_settings;
        const { device } = meta;

        const tofUnit = this.getIntervalUnitForValue(initialSettings.tof_interval, device);
        const ptcUnit = this.getIntervalUnitForValue(initialSettings.ptc_interval ?? 0, device);
        const stUnit = this.getIntervalUnitForValue(initialSettings.st_interval ?? 0, device);

        return tofUnit === ptcUnit && tofUnit === stUnit;
    }

    private async generateReportCanvas(): Promise<HTMLCanvasElement | null> {
        if (!this.reportContent?.nativeElement) {
            console.error('Report content not found');
            return null;
        }

        const element = this.reportContent.nativeElement;

        // Store original styles
        const originalWidth = element.style.width;
        const originalMaxWidth = element.style.maxWidth;
        const originalOverflow = element.style.overflow;

        try {
            // Set fixed width for consistent rendering
            element.style.width = '1200px';
            element.style.maxWidth = '1200px';
            element.style.overflow = 'hidden';

            // Wait for any layout changes to complete
            await new Promise((resolve) => setTimeout(resolve, 100));

            // Generate canvas from HTML
            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                logging: false,
                backgroundColor: '#ffffff',
                width: 1200
            });

            return canvas;
        } finally {
            // Restore original styles
            element.style.width = originalWidth;
            element.style.maxWidth = originalMaxWidth;
            element.style.overflow = originalOverflow;
        }
    }

    private async loadLogo(): Promise<{ dataUrl: string; w: number; h: number } | null> {
        return new Promise<{ dataUrl: string; w: number; h: number } | null>((resolve) => {
            const img = new Image();
            img.onload = () => {
                const w = img.naturalWidth || 400;
                const h = img.naturalHeight || 80;
                const c = document.createElement('canvas');
                c.width = w;
                c.height = h;
                c.getContext('2d')?.drawImage(img, 0, 0, w, h);
                resolve({ dataUrl: c.toDataURL('image/png'), w, h });
            };
            img.onerror = () => resolve(null);
            img.src = 'assets/images/TetraConnect_logo.svg';
        });
    }

    public async printReport(): Promise<void> {
        try {
            const [canvas, logo] = await Promise.all([this.generateReportCanvas(), this.loadLogo()]);
            if (!canvas) return;

            const imgData = canvas.toDataURL('image/png');
            const serial = this.measurementData?.meta?.serial ?? '';
            const date = this.getFormattedDate();
            const parts = [serial, date].filter(Boolean);
            const base = this.translateService.instant('FEATURES.MEASUREMENTS.REPORT_ACTIONS.PDF_HEADER') as string;
            const headerText = parts.length ? `${base} &middot; ${parts.join(' &middot; ')}` : base;
            const logoHtml = logo ? `<img src="${logo.dataUrl}" alt="Logo" style="height:40px;" />` : '';

            const printWindow = window.open('', '_blank');
            if (!printWindow) {
                alert('Please allow pop-ups to print the report');
                return;
            }

            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>&nbsp;</title>
                    <style>
                        body { margin: 0; padding: 0; background: white; font-family: helvetica, sans-serif; }
                        .header { display: flex; justify-content: space-between; align-items: center; padding: 8px 12px; border-bottom: 1px solid #ddd; margin-bottom: 12px; }
                        .header-text { color: #888; font-size: 12px; }
                        img.report { max-width: 100%; height: auto; display: block; }
                        @media print {
                            @page { margin: 0; }
                            body { margin: 0; padding: 0; }
                            .header { padding: 4px 8px; margin-bottom: 8px; }
                            img.report { max-width: 100%; page-break-inside: avoid; }
                        }
                    </style>
                </head>
                <body>
                    <div class="header">
                        ${logoHtml}
                        <span class="header-text">${headerText}</span>
                    </div>
                    <img class="report" src="${imgData}" alt="Measurement Report" />
                </body>
                </html>
            `);

            printWindow.document.close();

            printWindow.onload = () => {
                setTimeout(() => {
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                }, 500);
            };
        } catch (error) {
            console.error('Error preparing print:', error);
            const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
            const message = this.translateService.instant(
                'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DOWNLOAD_MEASUREMENT_ERROR'
            ) as string;
            this.toastService.open(title, message, { type: 'error', progressBar: true });
        }
    }

    public async downloadReportPDF(): Promise<void> {
        this.downloading.pdf = true;
        this.changeDetectorRef.detectChanges();

        try {
            const [canvas, logo] = await Promise.all([this.generateReportCanvas(), this.loadLogo()]);
            if (!canvas) return;

            const imgData = canvas.toDataURL('image/png');
            const pageWidth = 210;
            const pageHeight = 297;
            const pad = 6;
            const logoHeightMM = 10;
            const headerHeightMM = logo ? logoHeightMM + pad * 2 : 0;
            const imgHeight = (canvas.height * pageWidth) / canvas.width;

            const pdf = new jsPDF('p', 'mm', 'a4');

            const addHeader = () => {
                if (logo) {
                    const logoWidthMM = (logo.w / logo.h) * logoHeightMM;
                    pdf.addImage(logo.dataUrl, 'PNG', pad, pad, logoWidthMM, logoHeightMM);
                }
                const serial = this.measurementData?.meta?.serial ?? '';
                const date = this.getFormattedDate();
                const parts = [serial, date].filter(Boolean);
                const base = this.translateService.instant('FEATURES.MEASUREMENTS.REPORT_ACTIONS.PDF_HEADER') as string;
                const headerText = parts.length ? `${base}  ·  ${parts.join('  ·  ')}` : base;
                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(10);
                pdf.setTextColor(120, 120, 120);
                pdf.text(headerText, pageWidth - pad, pad + logoHeightMM / 2 + 1, { align: 'right' });
                pdf.setDrawColor(220, 220, 220);
                pdf.line(pad, headerHeightMM, pageWidth - pad, headerHeightMM);
                pdf.setTextColor(0, 0, 0);
            };

            addHeader();

            // Page 1: content offset below header
            const availableFirstPage = pageHeight - headerHeightMM;
            pdf.addImage(imgData, 'PNG', 0, headerHeightMM, pageWidth, imgHeight);
            let heightLeft = imgHeight - availableFirstPage;
            let yOffset = availableFirstPage;

            // Additional pages
            while (heightLeft > 0) {
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, -yOffset, pageWidth, imgHeight);
                yOffset += pageHeight;
                heightLeft -= pageHeight;
            }

            const filename = `measurement-report-${this.measurementData?.meta?.serial}-${this.measurementData?.meta?.date}.pdf`;
            pdf.save(filename);
        } catch (error) {
            console.error('Error generating PDF:', error);
            const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
            const message = this.translateService.instant(
                'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DOWNLOAD_MEASUREMENT_ERROR'
            ) as string;
            this.toastService.open(title, message, { type: 'error', progressBar: true });
        } finally {
            this.downloading.pdf = false;
            this.changeDetectorRef.markForCheck();
        }
    }

    public exportToExcel(): void {
        this.downloadExportFile(ExportType.Excel);
    }

    public exportToCSV(): void {
        this.downloadExportFile(ExportType.CSV);
    }

    public downloadRawData(): void {
        this.downloading.raw = true;
        this.changeDetectorRef.detectChanges();

        this.apiClientService.downloadMeasurementExportFile$(this.selectedMeasurementId, ExportType.RawData).subscribe({
            next: (response) => {
                const contentDisposition = response.headers.get('Content-Disposition');
                const filename = `measurement-raw-data-${this.selectedMeasurementId}.json`; // fallback
                this.downloadFile(contentDisposition, filename, response);
            },
            error: (error) => {
                console.error('Error downloading raw data:', error);
                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                const message = this.translateService.instant(
                    'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DOWNLOAD_MEASUREMENT_ERROR'
                ) as string;
                this.toastService.open(title, message, { type: 'error', progressBar: true });
            },
            complete: () => {
                this.downloading.raw = false;
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    public downloadExportFile(fileType: ExportType): void {
        const downloadType = fileType === ExportType.Excel ? 'excel' : 'csv';
        const extension = fileType === ExportType.Excel ? 'xlsx' : 'csv';

        this.downloading[downloadType] = true;
        this.changeDetectorRef.detectChanges();

        this.apiClientService.downloadMeasurementExportFile$(this.selectedMeasurementId, fileType).subscribe({
            next: (response) => {
                const contentDisposition = response.headers.get('Content-Disposition');
                const filename = `measurement-export-${this.selectedMeasurementId}.${extension}`; // fallback
                this.downloadFile(contentDisposition, filename, response);
            },
            error: (error) => {
                console.error('Error downloading export file:', error);
                const title = this.translateService.instant('TOAST_TITLES.ERROR') as string;
                const message = this.translateService.instant(
                    'FEATURES.MEASUREMENTS.TOAST_MESSAGES.DOWNLOAD_MEASUREMENT_ERROR'
                ) as string;
                this.toastService.open(title, message, { type: 'error', progressBar: true });
            },
            complete: () => {
                this.downloading[downloadType] = false;
                this.changeDetectorRef.markForCheck();
            }
        });
    }

    private downloadFile(contentDisposition: string, filename: string, response: HttpResponse<Blob>) {
        if (contentDisposition) {
            // Try RFC 5987 format first (filename*=UTF-8''...)
            let filenameMatch = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
            if (filenameMatch?.[1]) {
                filename = decodeURIComponent(filenameMatch[1]);
            } else {
                // Fall back to standard format (filename="..." or filename=...)
                filenameMatch = contentDisposition.match(/filename="([^"]+)"|filename=([^;,\s]+)/i);
                if (filenameMatch?.[1] || filenameMatch?.[2]) {
                    filename = filenameMatch[1] || filenameMatch[2];
                }
            }
        }

        const blob = response.body;
        if (blob) {
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            a.click();
            window.URL.revokeObjectURL(url);
        }
        return filename;
    }
}
